import { isEqual } from 'lodash';
import {
  compact,
  intersection,
  invert,
  isEmpty,
  partition,
  set,
  snakeCase,
  startCase,
  uniqBy,
} from 'lodash/fp';

import { titleCase } from 'dashboard/lib/utils/stringFormat';
import {
  createInputStep,
  deepTransformKeys,
  flattenSteps,
  getPlatformObjectTypeFromJsonSchema,
} from 'dashboard/lib/workflows';
import {
  EMPTY_METHOD_CHAIN,
  methodChainToString,
  parseMethodChain,
} from 'dashboard/lib/workflows/method-chain';
import {
  generatePlatformObjectJsonSchema,
  getSchemaId,
  getWorkflowObjectKeyFromSchemaRef,
  MERGED_SCHEMA_ID,
} from 'dashboard/lib/workflows/platform-object';
import {
  BaseTrigger,
  BaseTriggerParams,
  InputSchemaType,
  TRIGGER_EVENT_NAME_TO_OBJECT,
  TRIGGER_OBJECT_TYPE_TO_WORKFLOW_FIELD_OBJECT_KEY,
  TriggerAccountInputModalConfiguration,
  TriggerApi,
  TriggerCaseParamSchema,
  TriggerComputedField,
  TriggerEvent,
} from 'dashboard/models/trigger';
import {
  WorkflowFieldKey,
  WorkflowFieldObjectKey,
  WorkflowFieldVariableKey,
  WorkflowObjectKey,
} from 'dashboard/models/workflow';
import { WorkflowBlock } from 'dashboard/models/workflow-block';
import {
  GenericWorkflowAction,
  WorkflowCriteriaVariableSchemas,
} from 'dashboard/models/workflow-schema';
import {
  ContextualWorkflowStep,
  HttpPlatformIntegrationConfig,
  INPUT_STEP_TOKEN,
  NullWorkflowStepActionKey,
  StaticWorkflowAction,
  StaticWorkflowStepActionKey,
  VERIFICATION_MODEL_TYPE_TO_WORKFLOW_FIELD_OBJECT_KEY,
  WORKFLOW_ACTION_TO_OBJECT,
  WORKFLOW_ACTION_TO_VARIABLE,
  WorkflowAction,
  WorkflowActionCalculateV1,
  WorkflowActionEvaluateCodeReturnType,
  WorkflowActionEvaluateCodeV1,
  WorkflowActionEvaluateCodeV2,
  WorkflowActionFetchPersonaObjectV1,
  WorkflowActionHttpRequestV1,
  WorkflowActionQueryV1,
  WorkflowActionRunHttpPlatformIntegrationV1,
  WorkflowRunStepErrorType,
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepInput,
  WorkflowStepLoop,
  WorkflowStepLoopType,
  WorkflowStepOnErrorAction,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import {
  FieldSchema,
  FieldSchemaChoices,
  FieldSchemaHash,
  FieldSchemaType,
  getJsonSchemaForFieldSchema,
  JsonSchemaAllTypes,
  JsonSchemaCustomTypes,
} from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import {
  EMPTY_JSON_SCHEMA_OBJECT,
  isJsonSchemaArray,
  isJsonSchemaRef,
  isJsonSchemaString,
  JsonSchema,
  JsonSchemaArray,
  JsonSchemaBasicTypes,
  JsonSchemaEmpty,
  JsonSchemaObject,
  JsonSchemaTypes,
} from 'lib/json-schema';
import { assertNever } from 'lib/utils/types';
import { flattenBranch } from 'lib/workflows';

export type TemplateFilter = { type: ApiModel; id: string };
export type JsonSchemaFilters = {
  ['template-filters']?: TemplateFilter[];
  ['reject-computed']?: boolean;
} & Record<string, unknown>;

export const getActionStepFilters = (step?: WorkflowStepAction): JsonSchemaFilters => {
  if (step == null) return {};

  const filters: JsonSchemaFilters = {};
  const templateFilters: TemplateFilter[] = [];
  const action = step.action as StaticWorkflowAction;

  switch (action.key) {
    case WorkflowStepActionKey.CreateCase: {
      templateFilters.push({
        type: ApiModel.Case,
        id: action.params['case-template-token'] || MERGED_SCHEMA_ID,
      });
      break;
    }
    case WorkflowStepActionKey.CreateInquiry: {
      templateFilters.push({
        type: ApiModel.Inquiry,
        id: action.params['template-id'] || MERGED_SCHEMA_ID,
      });
      break;
    }
    case WorkflowStepActionKey.CreateTransaction: {
      templateFilters.push({
        type: ApiModel.Transaction,
        id: action.params['transaction-type-token'] || MERGED_SCHEMA_ID,
      });
      break;
    }
    case WorkflowStepActionKey.RunReportGeneric: {
      templateFilters.push({
        type: ApiModel.Report,
        id: action.params['report-template-token'] || MERGED_SCHEMA_ID,
      });
      break;
    }
    case WorkflowStepActionKey.FindOrCreateAccount: {
      // (evan.qi) this is a generic action, so params could be empty
      if (action.params && action.params['account-type-token']) {
        templateFilters.push({
          type: ApiModel.Account,
          id: action.params['account-type-token'] || MERGED_SCHEMA_ID,
        });
      }
      break;
    }
  }

  if (compact(templateFilters).length) {
    filters['template-filters'] = templateFilters;
  }

  return filters;
};

const getTriggerEventFilters = (
  trigger: TriggerEvent,
  workflowObjectKey: WorkflowFieldObjectKey,
) => {
  let templateFilters: TemplateFilter[] = [];
  const filters: JsonSchemaFilters = {};

  switch (workflowObjectKey) {
    case WorkflowFieldObjectKey.Transaction: {
      templateFilters = (trigger.attributes['template-filters']?.['transaction-type'] || []).map(
        (id) => ({
          type: ApiModel.Transaction,
          id,
        }),
      );
      break;
    }
    // TODO @jkim - add cases for inquiry and case
  }

  if (compact(templateFilters).length) {
    filters['template-filters'] = templateFilters;
  }

  return filters;
};

const getTriggerComputedFieldFilters = (trigger: TriggerComputedField): JsonSchemaFilters => ({
  'reject-computed': true,
  'template-filters': [
    {
      type: ApiModel.Transaction,
      id: trigger.attributes['field-schema-object-token'],
    },
  ],
});

export type UsableObjectConfig = {
  filters?: JsonSchemaFilters;
  isArray?: boolean;
  label: string;
  schema?: JsonSchema | null;
  step: ContextualWorkflowStep;
  token: string;
  tokenDisplayValue?: string;
  type?: JsonSchemaBasicTypes;
  value: string;
  workflowObjectKey: WorkflowObjectKey;
};

export const WORKFLOW_VARIABLE_TO_SUFFIX_TEXT: Record<WorkflowFieldVariableKey, string> = {
  [WorkflowFieldVariableKey.Calculate]: 'Calculated result',
  [WorkflowFieldVariableKey.CalculateVariable]: '',
  [WorkflowFieldVariableKey.Code]: 'Code result',
  [WorkflowFieldVariableKey.Matched]: 'List match result',
  [WorkflowFieldVariableKey.HttpRequest]: 'HTTP response',
  [WorkflowFieldVariableKey.MxUser]: 'MX user',
  [WorkflowFieldVariableKey.MxMicrodepositStatus]: 'MX micro-deposit status',
  [WorkflowFieldVariableKey.MxWidgetUrl]: 'MX widget URL',
  [WorkflowFieldVariableKey.Query]: 'Query step result',
  [WorkflowFieldVariableKey.SalesforceRecord]: 'Record ID',
  [WorkflowFieldVariableKey.StandardizeIdDetails]: 'Standardized ID details',
  [WorkflowFieldVariableKey.ZendeskTicket]: 'Ticket ID',
  [WorkflowFieldVariableKey.GptGeneratedMessage]: 'Message',
  [WorkflowFieldVariableKey.IntegrationObjectId]: 'Integration object ID',
  [WorkflowFieldVariableKey.HubspotObjectId]: 'Hubspot object ID',
  [WorkflowFieldVariableKey.Loop]: 'Input array',
  [WorkflowFieldVariableKey.MapOutput]: 'Array map output',
  [WorkflowFieldVariableKey.LoopCurrentItem]: '',
  [WorkflowFieldVariableKey.Analytics]: '',
  [WorkflowFieldVariableKey.InquiryFlowStateProperty]: '',
  [WorkflowFieldVariableKey.TriggerAccount]: '',
  [WorkflowFieldVariableKey.TriggerApi]: '',
  [WorkflowFieldVariableKey.TriggerCase]: '',
  [WorkflowFieldVariableKey.InputsHash]: '',
  [WorkflowFieldVariableKey.InputVariable]: '',
  [WorkflowFieldVariableKey.PlatformIntegration]: '',
  [WorkflowFieldVariableKey.Module]: '',
};

const generateVariableLabel = (name: string, workflowVariableKey: WorkflowFieldVariableKey) => {
  const formattedName = startCase(name);
  const suffixText = WORKFLOW_VARIABLE_TO_SUFFIX_TEXT[workflowVariableKey];
  if (!suffixText) {
    return formattedName;
  }
  return `${formattedName} (${suffixText})`;
};

const getWorkflowObjectKey = (step: WorkflowStepAction) => {
  if (!step.action) {
    return null;
  }

  switch (step.action.key) {
    case WorkflowStepActionKey.RunVerificationGeneric:
      return VERIFICATION_MODEL_TYPE_TO_WORKFLOW_FIELD_OBJECT_KEY[
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        step.action.params['verification-type']
      ];
    case WorkflowStepActionKey.FetchPersonaObject: {
      const action = step.action as WorkflowActionFetchPersonaObjectV1;
      if (action.params['object-type'] == null) {
        return null;
      }

      return (() => {
        switch (action.params['object-type']) {
          case ApiModel.Account: {
            return WorkflowFieldObjectKey.Account;
          }
          case ApiModel.Inquiry: {
            return WorkflowFieldObjectKey.Inquiry;
          }
          case ApiModel.Case: {
            return WorkflowFieldObjectKey.Case;
          }
          case ApiModel.Report: {
            return action.params['subtype'] || WorkflowFieldObjectKey.ReportAddressLookup;
          }
          case ApiModel.Verification: {
            return action.params['subtype'] || WorkflowFieldObjectKey.VerificationAamva;
          }
          case ApiModel.Transaction: {
            return WorkflowFieldObjectKey.Transaction;
          }
          case ApiModel.SAR: {
            return WorkflowFieldObjectKey.SAR;
          }
          default:
            assertNever(action.params['object-type']);
        }
      })();
    }
    default:
      return WORKFLOW_ACTION_TO_OBJECT[step.action.key as StaticWorkflowStepActionKey];
  }
};

export const getAllObjectsForStep = ({
  step,
  criteriaVariableSchemas,
  moduleBlock,
  includeBlockInnerContext,
  platformObjectJsonSchema,
  showErrorObjects = false,
}: {
  step: ContextualWorkflowStep;
  criteriaVariableSchemas: WorkflowCriteriaVariableSchemas;
  platformObjectJsonSchema: ObjectSchemaMap | undefined;
  moduleBlock?: WorkflowBlock;
  includeBlockInnerContext?: boolean;
  showErrorObjects?: boolean;
}): UsableObjectConfig[] => {
  const getAssociatedObjectOptionsFor = ({
    workflowObjectKey,
    primaryName,
    primaryKey,
    step,
    isArray,
  }: {
    workflowObjectKey: WorkflowFieldObjectKey;
    primaryName: string;
    primaryKey: string;
    step: ContextualWorkflowStep;
    isArray?: boolean;
  }): UsableObjectConfig[] =>
    platformObjectJsonSchema
      ? Object.entries(
          (
            platformObjectJsonSchema[getSchemaId(workflowObjectKey, 'merged')] ??
            platformObjectJsonSchema[getSchemaId(workflowObjectKey)] ??
            EMPTY_JSON_SCHEMA_OBJECT
          ).properties,
        )
          .filter(([_, propertySchema]) => isJsonSchemaRef(propertySchema))
          .map(([propertyKey, propertySchema]) => ({
            label: [
              primaryName,
              propertySchema.description ? `(${startCase(propertySchema.description)})` : '',
            ].join(' '),
            value: [primaryKey, propertyKey].join('.'),
            token: primaryKey,
            workflowObjectKey: getWorkflowObjectKeyFromSchemaRef(propertySchema.$ref as string),
            step,
            isArray,
          }))
      : (criteriaVariableSchemas[workflowObjectKey]?.associations ?? [])
          .filter(({ cardinality }) => cardinality === 'one')
          .map((association) => ({
            label: `${primaryName} (${startCase(association.type)})`,
            value: [primaryKey, association.key].join('.'),
            token: primaryKey,
            workflowObjectKey: association.type,
            step,
            isArray,
          }));

  const getObjectAndRelatedObjectsForStep = (
    step: WorkflowStep,
    workflowObjectKey: WorkflowFieldObjectKey,
  ) => {
    const option: UsableObjectConfig = {
      label: `${startCase(step.name)} (${startCase(workflowObjectKey)})`,
      value: step.token,
      token: step.token,
      workflowObjectKey,
      step,
      tokenDisplayValue: snakeCase(step.name),
    };

    if ('action' in step) {
      option.filters = getActionStepFilters(step);
    }

    return [
      option,
      ...getAssociatedObjectOptionsFor({
        workflowObjectKey,
        primaryName: startCase(step?.name || ''),
        primaryKey: step.token,
        step,
      }),
    ];
  };

  const errorObject: UsableObjectConfig = {
    label: `${startCase(step.name)} (Error)`,
    step,
    token: step.token,
    value: `${step.token}.error`,
    tokenDisplayValue: snakeCase(step.name),
    workflowObjectKey: WorkflowFieldObjectKey.Error,
  };

  switch (step.type) {
    case WorkflowStepType.Action: {
      if (!step.action) {
        return [];
      }

      const isStepContinueOnError =
        step['on-error'] && step['on-error'] === WorkflowStepOnErrorAction.Continue;
      const shouldAppendErrorObject = showErrorObjects && isStepContinueOnError;

      const maybeErrorObjectList = shouldAppendErrorObject ? [errorObject] : [];

      const specificActionKey = (() => {
        switch (step.action.key) {
          case WorkflowStepActionKey.RunReportGeneric:
            return step.action.params['report-step-action-key'] || step.action.key;
          case WorkflowStepActionKey.RunVerificationGeneric:
            // deprecated. Verification usable object is mapped from params['verification-type']
            // this is to support legacy configs
            return step.action.params['verification-step-action-key'] || step.action.key;
          default:
            return step.action.key;
        }
      })();
      const specificAction = {
        ...step,
        action: {
          ...step.action,
          key: specificActionKey,
        } as WorkflowAction,
      };
      const workflowObjectKey = getWorkflowObjectKey(specificAction);
      if (workflowObjectKey) {
        return [
          ...getObjectAndRelatedObjectsForStep(specificAction, workflowObjectKey),
          ...maybeErrorObjectList,
          // this should only return objects for direct workflow actions
          ...generateInquiryFlowStatePropertyUsableObjectsFromSteps([step]),
        ];
      }

      const workflowVariableKey =
        WORKFLOW_ACTION_TO_VARIABLE[
          specificActionKey as
            | StaticWorkflowStepActionKey
            | WorkflowActionRunHttpPlatformIntegrationV1['key']
        ];
      if (workflowVariableKey) {
        if (workflowVariableKey === WorkflowFieldVariableKey.Calculate) {
          const action = (step as WorkflowStepAction).action as WorkflowActionCalculateV1;
          const returnVariables = action.params['return-variables'];
          if (!returnVariables.length) {
            return [];
          }
          return [
            {
              label: generateVariableLabel(step.name, WorkflowFieldVariableKey.Calculate),
              value: step.token,
              token: step.token,
              workflowObjectKey: WorkflowFieldVariableKey.Calculate,
              step,
              tokenDisplayValue: snakeCase(step.name),
            },
            ...returnVariables.map((returnVariableId): UsableObjectConfig => {
              const { type, name } =
                action.params.variables.find(({ id }) => id === returnVariableId) || {};
              return {
                label: name || '',
                token: returnVariableId,
                value: `${step.token}.output.${returnVariableId}`,
                workflowObjectKey: WorkflowFieldVariableKey.CalculateVariable,
                schema: type ? { type } : undefined,
                tokenDisplayValue: snakeCase(name || ''),
                step,
              };
            }),
            ...maybeErrorObjectList,
          ];
        }

        return [
          {
            label: generateVariableLabel(step.name, workflowVariableKey),
            value: step.token,
            token: step.token,
            workflowObjectKey: workflowVariableKey,
            step,
            tokenDisplayValue: snakeCase(step.name),
          },
          ...maybeErrorObjectList,
        ];
      }

      // For actions that don't have a specific object key, we default to generic in order to surface the error obj
      const genericObject: UsableObjectConfig = {
        label: startCase(step.name),
        step,
        token: step.token,
        value: step.token,
        tokenDisplayValue: snakeCase(step.name),
        workflowObjectKey: WorkflowFieldObjectKey.Generic,
      };
      return shouldAppendErrorObject && step.action.key !== NullWorkflowStepActionKey.NullKey
        ? [genericObject, errorObject]
        : [];
    }
    case WorkflowStepType.Loop: {
      const loopObjects: UsableObjectConfig[] = [
        {
          label: generateVariableLabel(step.name, WorkflowFieldVariableKey.Loop),
          value: step.token,
          token: step.token,
          workflowObjectKey: WorkflowFieldVariableKey.Loop,
          step,
          tokenDisplayValue: snakeCase(step.name),
        },
      ];
      const workflowObjectKey =
        (step.settings.type === WorkflowStepLoopType.ForEach ||
          step.settings.type === WorkflowStepLoopType.Map) &&
        step.settings['input-array-schema']?.type === JsonSchemaTypes.Array &&
        'objectKey' in step.settings['input-array-schema'].items &&
        step.settings['input-array-schema'].items.objectKey
          ? step.settings['input-array-schema'].items.objectKey
          : WorkflowFieldVariableKey.LoopCurrentItem;
      const label = isWorkflowFieldObjectKey(workflowObjectKey)
        ? `${startCase(step.name)} - Current Item (${startCase(workflowObjectKey)})`
        : `${startCase(step.name)} - Current Item`;

      const currentItemObject = {
        label,
        value: `${step.token}.output.current_item`,
        token: step.token,
        workflowObjectKey,
        step,
        tokenDisplayValue: snakeCase(step.name),
      };
      if (step.settings.type === WorkflowStepLoopType.ForEach) {
        loopObjects.push(currentItemObject);
      } else if (step.settings.type === WorkflowStepLoopType.Map) {
        loopObjects.push(currentItemObject);
        loopObjects.push({
          label: generateVariableLabel(step.name, WorkflowFieldVariableKey.MapOutput),
          value: step.token,
          token: step.token,
          workflowObjectKey: WorkflowFieldVariableKey.MapOutput,
          step,
          tokenDisplayValue: snakeCase(step.name),
        });
      }
      return loopObjects;
    }
    case WorkflowStepType.Module: {
      if (!moduleBlock || moduleBlock.id !== step['child-workflow-block-id']) {
        return [];
      }

      const moduleUsableObjects = generateUsableObjects({
        inputStep: createInputStep({ inputs: moduleBlock.attributes.inputs }),
        stepsToProcess: flattenSteps(moduleBlock.attributes['steps-config']),
        criteriaVariableSchemas,
      });
      const moduleOutputObjects: UsableObjectConfig[] = compact(
        moduleUsableObjects.map((usableObject) => {
          const moduleOutput = moduleBlock.attributes.outputs.find((output) =>
            usableObject.value.startsWith(output['method-chain']),
          );
          if (!moduleOutput) {
            return null;
          }
          /**
           * Replace the original token with the output token.
           * We use string replace to ensure we preserve any nested objects.
           * If a create inquiry step has token abc123 within the module, and it is outputted with token def456,
           * we want the module outputs to be:
           * - def456
           * - def456.account
           * - def456.environment
           */
          const newValue = usableObject.value.replace(
            moduleOutput['method-chain'],
            moduleOutput.token,
          );
          return {
            ...usableObject,
            value: `${step.token}.${newValue}`,
            token: moduleOutput.token,
            tokenDisplayValue: snakeCase(moduleOutput.name),
          };
        }),
      );
      if (!moduleOutputObjects.length) {
        return includeBlockInnerContext ? moduleUsableObjects : [];
      }

      const isStepContinueOnError =
        step['on-error'] && step['on-error'] === WorkflowStepOnErrorAction.Continue;
      const shouldAppendErrorObject = showErrorObjects && isStepContinueOnError;

      return [
        {
          label: generateVariableLabel(step.name, WorkflowFieldVariableKey.Module),
          value: step.token,
          token: step.token,
          workflowObjectKey: WorkflowFieldVariableKey.Module,
          step,
          tokenDisplayValue: snakeCase(step.name),
        },
        ...moduleOutputObjects,
        ...(includeBlockInnerContext ? moduleUsableObjects : []),
        ...(shouldAppendErrorObject ? [errorObject] : []),
      ];
    }
    case WorkflowStepType.Conditional:
    case WorkflowStepType.Parallel:
    case WorkflowStepType.Wait:
      return [];
    case WorkflowStepType.Input: {
      const { trigger, inputs, filter } = step;
      const inputOptions: UsableObjectConfig[] = inputs.length
        ? [
            {
              label: 'Input Data',
              value: step.token,
              token: step.token,
              workflowObjectKey: WorkflowFieldVariableKey.InputsHash,
              step,
            },
            ...inputs.flatMap(({ token, name, schema }): UsableObjectConfig[] => {
              const jsonSchema = JSON.parse(schema);
              const formattedName = startCase(name);
              const value = `${step.token}.${token}`;
              const isArray = jsonSchema.type === JsonSchemaTypes.Array;
              const workflowObjectKey = getPlatformObjectTypeFromJsonSchema(
                isArray ? jsonSchema.items : jsonSchema,
              );

              if (workflowObjectKey) {
                return [
                  {
                    label: `${formattedName} (${startCase(workflowObjectKey)})`,
                    token,
                    value,
                    workflowObjectKey,
                    tokenDisplayValue: snakeCase(name),
                    step,
                    isArray,
                  },
                  ...getAssociatedObjectOptionsFor({
                    workflowObjectKey,
                    primaryName: formattedName,
                    primaryKey: value,
                    step,
                    isArray,
                  }),
                ];
              }

              return [
                {
                  label: formattedName,
                  token,
                  value,
                  workflowObjectKey: WorkflowFieldVariableKey.InputVariable,
                  schema: jsonSchema,
                  tokenDisplayValue: snakeCase(name),
                  step,
                },
              ];
            }),
          ]
        : [];

      const triggerOptions: UsableObjectConfig[] = (() => {
        switch (trigger?.type) {
          case ApiModel.TriggerDirect: {
            const workflowObjectKey = WorkflowFieldObjectKey.Inquiry;
            const triggerOption: UsableObjectConfig = {
              label: `Trigger (${startCase(workflowObjectKey)})`,
              value: BaseTrigger,
              token: BaseTrigger,
              step,
              workflowObjectKey,
              filters: filter ? { 'template-filters': [filter] } : undefined,
            };

            return [
              triggerOption,
              ...getAssociatedObjectOptionsFor({
                workflowObjectKey,
                primaryName: 'Trigger',
                primaryKey: BaseTrigger,
                step,
              }),
            ];
          }
          case ApiModel.TriggerCase: {
            const workflowObjectKey = WorkflowFieldObjectKey.Case;
            return [
              {
                label: 'Trigger (Case)',
                value: BaseTrigger,
                token: BaseTrigger,
                step,
                workflowObjectKey: WorkflowFieldObjectKey.Case,
              },
              {
                label: 'Trigger Params',
                value: BaseTriggerParams,
                token: BaseTriggerParams,
                step,
                workflowObjectKey: WorkflowFieldVariableKey.TriggerCase,
              },
              ...getAssociatedObjectOptionsFor({
                workflowObjectKey,
                primaryName: 'Trigger',
                primaryKey: BaseTrigger,
                step,
              }),
            ];
          }
          case ApiModel.TriggerAccount: {
            return [
              {
                label: 'Trigger (Account)',
                value: BaseTrigger,
                token: BaseTrigger,
                step,
                workflowObjectKey: WorkflowFieldObjectKey.Account,
              },
              {
                label: 'Trigger Params',
                value: BaseTriggerParams,
                token: BaseTriggerParams,
                step,
                workflowObjectKey: WorkflowFieldVariableKey.TriggerAccount,
              },
            ];
          }
          case ApiModel.TriggerApi: {
            return [
              {
                label: 'Trigger (API)',
                value: BaseTriggerParams,
                token: BaseTriggerParams,
                step,
                workflowObjectKey: WorkflowFieldVariableKey.TriggerApi,
              },
            ];
          }
          case ApiModel.TriggerEvent: {
            const triggerEventName = trigger.attributes['event-name'];
            const workflowObjectKey = TRIGGER_EVENT_NAME_TO_OBJECT[triggerEventName];
            const triggerOption = {
              label: `Trigger (${startCase(workflowObjectKey)})`,
              value: BaseTrigger,
              token: BaseTrigger,
              step,
              workflowObjectKey,
              filters: getTriggerEventFilters(trigger, workflowObjectKey),
            };

            return [
              triggerOption,
              ...getAssociatedObjectOptionsFor({
                workflowObjectKey,
                primaryName: 'Trigger',
                primaryKey: BaseTrigger,
                step,
              }),
            ];
          }
          case ApiModel.TriggerComputedField: {
            const eventName = trigger.attributes['config']?.[0];
            if (eventName) {
              const workflowObjectKey = TRIGGER_EVENT_NAME_TO_OBJECT[eventName];
              const triggerOption = {
                label: `Trigger (${startCase(workflowObjectKey)})`,
                value: BaseTrigger,
                token: BaseTrigger,
                step,
                workflowObjectKey,
                filters: getTriggerComputedFieldFilters(trigger),
              };

              return [
                triggerOption,
                ...getAssociatedObjectOptionsFor({
                  workflowObjectKey,
                  primaryName: 'Trigger',
                  primaryKey: BaseTrigger,
                  step,
                }),
              ];
            }
            return [];
          }
          case ApiModel.TriggerRecurring: {
            const workflowObjectKey =
              TRIGGER_OBJECT_TYPE_TO_WORKFLOW_FIELD_OBJECT_KEY[
                trigger.attributes['trigger-object-type']
              ];
            const triggerOption = {
              label: `Trigger (${startCase(workflowObjectKey)})`,
              value: BaseTrigger,
              token: BaseTrigger,
              step,
              workflowObjectKey,
            };

            return [
              triggerOption,
              ...getAssociatedObjectOptionsFor({
                workflowObjectKey,
                primaryName: 'Trigger',
                primaryKey: BaseTrigger,
                step,
              }),
            ];
          }
          case ApiModel.TriggerSchedule: {
            const workflowObjectKey =
              TRIGGER_OBJECT_TYPE_TO_WORKFLOW_FIELD_OBJECT_KEY[
                trigger.attributes['trigger-object-type']
              ];
            const triggerOption = {
              label: `Trigger (${startCase(workflowObjectKey)})`,
              value: BaseTrigger,
              token: BaseTrigger,
              step,
              workflowObjectKey,
            };

            return [
              triggerOption,
              ...getAssociatedObjectOptionsFor({
                workflowObjectKey,
                primaryName: 'Trigger',
                primaryKey: BaseTrigger,
                step,
              }),
            ];
          }
          default:
            return [];
        }
      })();

      return [...triggerOptions, ...inputOptions];
    }
    default:
      return [];
  }
};

type UsableObjectFilterOptions = {
  permittedObjects?: WorkflowFieldObjectKey[] | null;
  excludeNonPersonaObjects?: boolean;
};
/**
 *
 * @param {WorkflowStepInput} inputStep - config for the initial step
 * @param {WorkflowStep[]} stepsToProcess - steps in path to the node we want to generate conditionals for
 * @param {WorkflowStep} activeStep - used to filter out loop steps that are not the parent of the active step. If activeStep is null, all loop step outputs are excluded
 * @param {{ permittedObjects: WorkflowFieldKey, excludeNonPersonaObjects: boolean }} - permittedObjects is to return only expected object types.
 * excludeNonPersonaObjects is to filter out non-Persona objects like trigger_params, calculation results
 *
 * @returns display options for input field for Workflows 2.0 conditionals
 *
 * Returns all steps in path + associated objects to steps + trigger and trigger associations.
 *
 */
export const generateUsableObjects = ({
  inputStep,
  stepsToProcess,
  activeStepToken,
  criteriaVariableSchemas,
  platformObjectJsonSchema,
  showErrorObjects,
}: {
  inputStep?: WorkflowStepInput;
  stepsToProcess: WorkflowStep[];
  activeStepToken?: string;
  criteriaVariableSchemas: WorkflowCriteriaVariableSchemas;
  platformObjectJsonSchema?: ObjectSchemaMap | undefined;
  showErrorObjects?: boolean;
}): UsableObjectConfig[] => {
  const usableObjectsByKey: Record<string, UsableObjectConfig[]> = {
    [INPUT_STEP_TOKEN]: inputStep
      ? getAllObjectsForStep({
          step: inputStep,
          criteriaVariableSchemas,
          platformObjectJsonSchema,
          showErrorObjects,
        })
      : [],
  };
  stepsToProcess.forEach((step) => {
    const usableObjects = getAllObjectsForStep({
      step,
      criteriaVariableSchemas,
      platformObjectJsonSchema,
      showErrorObjects,
    });
    if (usableObjects.length) {
      usableObjectsByKey[step.token] = usableObjects;
    }
  });

  return getActualStepObjects({ usableObjectsByKey, activeStepToken });
};

export const getActualStepObjects = ({
  usableObjectsByKey,
  activeStepToken,
}: {
  usableObjectsByKey: Record<string, UsableObjectConfig[]>;
  activeStepToken?: string;
}) => {
  const { [INPUT_STEP_TOKEN]: inputObjects, ...stepObjects } = usableObjectsByKey;
  const actualStepObjects = Object.values(stepObjects).flatMap((objects) => {
    if (!objects.length) {
      return [];
    }
    const { step } = objects[0];
    if (step.type === WorkflowStepType.Loop) {
      const [outsideObjects, insideObjects] = partition(
        ({ workflowObjectKey }) => workflowObjectKey === WorkflowFieldVariableKey.MapOutput,
        objects,
      );
      if (
        step.settings.type === WorkflowStepLoopType.Map &&
        (!activeStepToken ||
          flattenBranch(step.branch).every((branchStep) => branchStep?.token !== activeStepToken))
      ) {
        // This means the target node is outside the loop nested scope
        return outsideObjects;
      }
      if (
        activeStepToken &&
        flattenBranch(step.branch).some((branchStep) => branchStep?.token === activeStepToken)
      ) {
        // This means the target node is inside the loop nested scope
        return insideObjects;
      }
      return [];
    }
    return objects;
  });
  return [...inputObjects, ...actualStepObjects];
};

export const filterUsableObjects = ({
  usableObjects,
  options,
}: {
  usableObjects: UsableObjectConfig[];
  options: UsableObjectFilterOptions;
}) => {
  const { permittedObjects, excludeNonPersonaObjects } = options;
  const isPermitted = (objectKey: WorkflowObjectKey) =>
    !isWorkflowFieldObjectKey(objectKey) ||
    !permittedObjects ||
    permittedObjects.includes(objectKey);

  return usableObjects.filter(({ workflowObjectKey }) => {
    if (!isPermitted(workflowObjectKey)) {
      return false;
    }
    return !(
      excludeNonPersonaObjects &&
      workflowObjectKey &&
      isWorkflowFieldVariableKey(workflowObjectKey)
    );
  });
};

/**
 * @returns array of flow state property usable objects associated with a step
 */
export const generateInquiryFlowStatePropertyUsableObjectsFromSteps = (
  steps: WorkflowStep[],
): UsableObjectConfig[] => {
  return steps.flatMap((step) => {
    if (
      step.type !== WorkflowStepType.Action ||
      step.action.key === NullWorkflowStepActionKey.NullKey
    ) {
      return [];
    }

    const objects: UsableObjectConfig[] = [];

    const getAttemptCountFlowStateKey = (step: WorkflowStepAction) => {
      switch (step.action.key) {
        case WorkflowStepActionKey.CreateVerificationAamvaFromInquiry:
          return `${step.token}_verification_aamva_attempts`;
        case WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry:
        case WorkflowStepActionKey.ProcessLatestVerificationCertificateKorea:
          return `${step.token}_verification_certificate_korea_attempts`;
        case WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry:
          return `${step.token}_verification_database`;
        case WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry:
        case WorkflowStepActionKey.ProcessLatestVerificationDatabaseAadhaar:
          return `${step.token}_verification_database_aadhaar_attempts`;
        case WorkflowStepActionKey.CreateVerificationDatabaseStandardFromInquiry:
          return `${step.token}_verification_database_standard`;
        case WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry:
          return `${step.token}_verification_database_ecbsv`;
        case WorkflowStepActionKey.CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry:
        case WorkflowStepActionKey.ProcessLatestVerificationDigitalIdFinnishTrustNetwork:
          return `${step.token}_verification_digital_id_finnish_trust_network_attempts`;
        case WorkflowStepActionKey.CreateVerificationDigitalIdOneIdFromInquiry:
        case WorkflowStepActionKey.ProcessLatestVerificationDigitalIdOneId:
          return `${step.token}_verification_digital_id_one_id_attempts`;
        case WorkflowStepActionKey.CreateVerificationDigitalIdSingpassFromInquiry:
        case WorkflowStepActionKey.ProcessLatestVerificationDigitalIdSingpass:
          return `${step.token}_verification_digital_id_singpass_attempts`;
        case WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry:
          return `${step.token}_verification_digital_id_uk_sharecode_attempts`;
        case WorkflowStepActionKey.CreateVerificationDocumentFromInquiry:
          return `${step.token}_verification_document`;
        case WorkflowStepActionKey.ConfirmLatestVerificationEmailAddress:
          return `${step.token}_verification_email_address_attempts`;
        case WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry:
          return `${step.token}_verification_government_id_attempts`;
        case WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry:
          return `${step.token}_verification_government_id_nfc_attempts`;
        case WorkflowStepActionKey.CreateVerificationSelfieFromInquiry:
          return `${step.token}_verification_selfie`;
        case WorkflowStepActionKey.RunVerificationDatabaseBusiness:
          return `${step.token}_verification_database_business`;
        case WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry:
          return `${step.token}_verification_database_business_footprint`;
        case WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry:
          return `${step.token}_verification_business_website`;
        case WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry:
          return `${step.token}_verification_database_phone_carrier_attempts`;
        case WorkflowStepActionKey.RunVerificationDatabaseSerproFromInquiry:
          return `${step.token}_verification_database_serpro_attempts`;
        case WorkflowStepActionKey.RunVerificationDatabaseTinFromInquiry:
          return `${
            step.action.params['flow-state-attempt-count-token'] ?? step.token
          }_verification_database_tin_attempts`;
        case WorkflowStepActionKey.RunVerificationDatabaseVatNumberFromInquiry:
          return `${step.token}_verification_database_vat_number_attempts`;
        default:
          return null;
      }
    };

    const getVerificationStatusFlowStateKey = (step: WorkflowStepAction) => {
      switch (step.action?.key) {
        case WorkflowStepActionKey.CreateVerificationAamvaFromInquiry:
          return `${step.token}_verification_aamva_status`;
        case WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry:
          return `${step.token}_verification_certificate_korea_status`;
        case WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry:
          return `${step.token}_verification_database_ecbsv_status`;
        case WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry:
          return `${step.token}_verification_database_status`;
        case WorkflowStepActionKey.CreateVerificationDatabaseStandardFromInquiry:
          return `${step.token}_verification_database_standard_status`;
        case WorkflowStepActionKey.CreateVerificationDocumentFromInquiry:
          return `${step.token}_verification_document_status`;
        case WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry:
          return `${step.token}_verification_government_id_status`;
        case WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry:
          return `${step.token}_verification_government_id_nfc_status`;
        case WorkflowStepActionKey.CreateVerificationSelfieFromInquiry:
          return `${step.token}_verification_selfie_status`;
        case WorkflowStepActionKey.ConfirmLatestVerificationEmailAddress:
          return `${step.token}_verification_email_address_status`;
        case WorkflowStepActionKey.ConfirmLatestVerificationPhoneNumber:
          return `${step.token}_verification_phone_number_status`;
        case WorkflowStepActionKey.RunVerificationDatabaseBusiness:
          return `${step.token}_verification_database_business_status`;
        case WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry:
          return `${step.token}_verification_database_business_footprint_status`;
        case WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry:
          return `${step.token}_verification_business_website`;
        case WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry:
          return `${step.token}_verification_database_phone_carrier_status`;
        case WorkflowStepActionKey.RunVerificationDatabaseSerproFromInquiry:
          return `${step.token}_verification_database_serpro_status`;
        case WorkflowStepActionKey.RunVerificationDatabaseTinFromInquiry:
          return `${step.token}_verification_database_tin_status`;
        case WorkflowStepActionKey.RunVerificationDatabaseVatNumberFromInquiry:
          return `${step.token}_verification_database_vat_number_status`;
        default:
          return null;
      }
    };

    const getErrorFlowStateKey = (step: WorkflowStepAction) => {
      switch (step.action.key) {
        case WorkflowStepActionKey.ConfirmLatestVerificationEmailAddress:
        case WorkflowStepActionKey.ConfirmLatestVerificationPhoneNumber:
        case WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry:
        case WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry:
          return `${step.token}_error`;
        default:
          return null;
      }
    };

    const attemptCountKey = getAttemptCountFlowStateKey(step);
    if (attemptCountKey) {
      objects.push({
        value: [BaseTrigger, 'flow_state', attemptCountKey].join('.'),
        type: JsonSchemaTypes.Integer,
        token: attemptCountKey,
        tokenDisplayValue: `${snakeCase(step.name)}_attempts`,
        label: `${startCase(step.name)} Attempts`,
        workflowObjectKey: WorkflowFieldVariableKey.InquiryFlowStateProperty,
        step,
      });
    }

    const verificationStatusKey = getVerificationStatusFlowStateKey(step);
    if (verificationStatusKey) {
      objects.push({
        value: [BaseTrigger, 'flow_state', verificationStatusKey].join('.'),
        type: JsonSchemaTypes.String,
        token: verificationStatusKey,
        tokenDisplayValue: `${snakeCase(step.name)}_status`,
        label: `${startCase(step.name)} Status`,
        workflowObjectKey: WorkflowFieldVariableKey.InquiryFlowStateProperty,
        step,
      });
    }

    const errorKey = getErrorFlowStateKey(step);
    if (errorKey) {
      objects.push({
        value: [BaseTrigger, 'flow_state', errorKey].join('.'),
        type: JsonSchemaTypes.Boolean,
        token: errorKey,
        tokenDisplayValue: `${snakeCase(step.name)}_errored`,
        label: `${startCase(step.name)} Errored`,
        workflowObjectKey: WorkflowFieldVariableKey.InquiryFlowStateProperty,
        step,
      });
    }

    return objects;
  });
};

export const accountAttachmentsFor = (usableObjects: UsableObjectConfig[]) => {
  return filterUsableObjects({
    usableObjects,
    options: {
      permittedObjects: [
        WorkflowFieldObjectKey.Inquiry,
        WorkflowFieldObjectKey.GraphQuery,
        WorkflowFieldObjectKey.SAR,
        ...REPORT_OBJECT_KEYS,
        ...VERIFICATION_OBJECT_KEYS,
      ],
      excludeNonPersonaObjects: true,
    },
  }).map(({ value, label }) => ({ value, label }));
};

export const caseAttachmentsFor = (usableObjects: UsableObjectConfig[]) => {
  return filterUsableObjects({
    usableObjects,
    options: {
      permittedObjects: [
        WorkflowFieldObjectKey.Account,
        WorkflowFieldObjectKey.GraphQuery,
        WorkflowFieldObjectKey.Inquiry,
        WorkflowFieldObjectKey.SAR,
        WorkflowFieldObjectKey.Transaction,
        ...REPORT_OBJECT_KEYS,
        ...VERIFICATION_OBJECT_KEYS,
      ],
      excludeNonPersonaObjects: true,
    },
  }).map(({ value, label }) => ({ value, label }));
};

export const isWorkflowFieldObjectKey = (
  key: WorkflowObjectKey | string,
): key is WorkflowFieldObjectKey => {
  return Object.values<WorkflowObjectKey | string>(WorkflowFieldObjectKey).includes(key);
};
export const isWorkflowFieldVariableKey = (
  key: WorkflowObjectKey,
): key is WorkflowFieldVariableKey => {
  return Object.values<WorkflowObjectKey>(WorkflowFieldVariableKey).includes(key);
};

export const getArrayJsonSchemaType = (
  schema: JsonSchemaArray,
): JsonSchemaBasicTypes | JsonSchemaTypes.Object | undefined => {
  const jsonSchemaType = getJsonSchemaType(schema);
  if (
    jsonSchemaType !== JsonSchemaTypes.Array ||
    !('type' in schema.items) ||
    schema.items.type == null
  ) {
    return undefined;
  }

  const schemaItemType: JsonSchemaTypes = schema.items.type;
  switch (schemaItemType) {
    // TODO: figure out how to support these at a later time
    case JsonSchemaTypes.Array:
      return undefined;
    case JsonSchemaTypes.Boolean:
    case JsonSchemaTypes.Integer:
    case JsonSchemaTypes.Number:
    case JsonSchemaTypes.Object:
    case JsonSchemaTypes.String:
      return schemaItemType;
    default:
      assertNever(schemaItemType);
  }
};

export const getJsonSchemaType = (
  schema: JsonSchema | null,
): JsonSchemaTypes | JsonSchemaCustomTypes => {
  if (schema == null) {
    return JsonSchemaAllTypes.Unknown;
  }

  if ('enum' in schema) {
    return JsonSchemaAllTypes.Enum;
  }
  if ('anyOf' in schema && schema.anyOf.every((group: JsonSchema) => 'enum' in group)) {
    return JsonSchemaAllTypes.GroupedEnum;
  }
  if ('type' in schema) {
    if (schema.type === JsonSchemaAllTypes.String && schema.format === 'date') {
      return JsonSchemaAllTypes.Date;
    }
    if (schema.type === JsonSchemaAllTypes.Array && schema.uniqueItems && 'enum' in schema.items) {
      return JsonSchemaAllTypes.MultiEnum;
    }
    if (isJsonSchemaString(schema) && 'oneOf' in schema) {
      return JsonSchemaAllTypes.Enum;
    }
    if (isJsonSchemaArray(schema) && isJsonSchemaString(schema.items) && 'oneOf' in schema.items) {
      return JsonSchemaAllTypes.MultiEnum;
    }
    return schema.type;
  }
  return JsonSchemaAllTypes.Unknown;
};

export const generateWorkflowObjectJsonSchema = ({
  criteriaVariableSchemas,
  objectKey,
  description,
  filters,
  nestedObjectKeys = [],
  isTopLevel = true,
  useNewMergeStrategy = false,
}: {
  criteriaVariableSchemas: WorkflowCriteriaVariableSchemas;
  objectKey: WorkflowFieldObjectKey;
  description: string;
  filters?: JsonSchemaFilters;
  nestedObjectKeys?: WorkflowFieldObjectKey[];
  isTopLevel?: boolean;
  useNewMergeStrategy?: boolean;
}): JsonSchemaObject => {
  const properties = Object.entries(
    getWorkflowObjectFieldSchema({
      criteriaVariableSchemas: criteriaVariableSchemas,
      objectKey: objectKey,
      filters: filters,
      useNewMergeStrategy: useNewMergeStrategy,
    }),
  ).reduce<Record<string, JsonSchema>>((hash, [key, fieldSchema]) => {
    return {
      ...hash,
      [key]: getJsonSchemaForFieldSchema(fieldSchema),
    };
  }, {});

  const propertiesForAssociations = (criteriaVariableSchemas[objectKey]?.associations ?? []).reduce<
    Record<string, JsonSchema>
  >((resultJsonSchema, association) => {
    const includeAssociation =
      // Create `many` and non-top level `one` associations
      (association.cardinality === 'many' || !isTopLevel) &&
      // Check existence in nestedObjectKeys for cycle prevention.
      !nestedObjectKeys.includes(association.type);

    if (includeAssociation) {
      const childrenSchema = generateWorkflowObjectJsonSchema({
        criteriaVariableSchemas: criteriaVariableSchemas,
        objectKey: association.type,
        description: `Type: ${titleCase(association.type)}`,
        filters: filters,
        nestedObjectKeys: [...nestedObjectKeys, objectKey],
        isTopLevel: false,
        useNewMergeStrategy: useNewMergeStrategy,
      });

      return {
        ...resultJsonSchema,

        [association.key]:
          association.cardinality === 'many'
            ? {
                type: JsonSchemaTypes.Array,
                description: `List of ${titleCase(association.type)}`,
                items: childrenSchema,
              }
            : childrenSchema,
      };
    }

    return { ...resultJsonSchema };
  }, {});

  return {
    type: JsonSchemaTypes.Object,
    description,
    objectKey,
    properties: deepTransformKeys({
      ...properties,
      ...propertiesForAssociations,
    }),
  };
};

const getWorkflowObjectFieldSchema = ({
  criteriaVariableSchemas,
  objectKey,
  filters,
  useNewMergeStrategy = false,
}: {
  criteriaVariableSchemas: WorkflowCriteriaVariableSchemas;
  objectKey: WorkflowObjectKey;
  filters?: JsonSchemaFilters;
  useNewMergeStrategy?: boolean;
}): Partial<Record<WorkflowFieldKey, FieldSchema>> => {
  const commonSchemas = criteriaVariableSchemas[objectKey]?.common || {};
  const withTemplateSchemas = criteriaVariableSchemas[objectKey]?.['with-template'] || {};

  let combinedSchemasAcrossTemplates = {};
  // NOTE - If template-filters exists and its value is an empty array, this means
  // we want to exclude all templates. The useAllTemplates case exists for objects
  // like inquiries where we do not know the template token until run time.
  const useAllTemplates = !filters || !filters['template-filters'];
  const mergeFunc = useNewMergeStrategy ? mergeTemplateSchemasNew : mergeTemplateSchemas;
  if (useAllTemplates) {
    combinedSchemasAcrossTemplates = mergeFunc(withTemplateSchemas);
  } else {
    const templateTokens = compact(
      (filters['template-filters'] || []).map(({ id }) => id?.replace(/_/g, '-')),
    );
    const missingTemplates =
      intersection(templateTokens, Object.keys(withTemplateSchemas)).length === 0;

    if (missingTemplates) {
      combinedSchemasAcrossTemplates = mergeFunc(withTemplateSchemas);
    } else if (templateTokens.length > 1) {
      const filteredTemplates = templateTokens.reduce(
        (accum, templateToken) => ({
          ...accum,
          [templateToken]: withTemplateSchemas[templateToken],
        }),
        {},
      );
      combinedSchemasAcrossTemplates = mergeFunc(filteredTemplates);
    } else {
      combinedSchemasAcrossTemplates = withTemplateSchemas[templateTokens[0]];
    }
  }

  return {
    ...commonSchemas,
    ...filterTemplateSchemas(combinedSchemasAcrossTemplates, filters),
  };
};

export const filterTemplateSchemas = (
  schemas: Partial<Record<WorkflowFieldKey, FieldSchema>>,
  filters?: JsonSchemaFilters,
): Partial<Record<WorkflowFieldKey, FieldSchema>> => {
  if (
    filters?.['reject-computed'] &&
    WorkflowFieldKey.Fields in schemas &&
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    schemas[WorkflowFieldKey.Fields].type === FieldSchemaType.Hash
  ) {
    const fieldsSchema = schemas[WorkflowFieldKey.Fields] as FieldSchemaHash;
    const itemSchemas = fieldsSchema.config['item-schemas'].filter(
      (schema) => !schema.config.computed,
    );
    return {
      ...schemas,
      [WorkflowFieldKey.Fields]: {
        ...fieldsSchema,
        config: {
          ...fieldsSchema.config,
          'item-schemas': itemSchemas,
        },
      },
    };
  }

  return schemas;
};

/**
 * We should technically be returning an intersection of fields across templates, but this likely results in
 * no fields being returned at all. For now, we include a field if:
 * 1. it exists on any template
 * 2. if on multiple templates, its schema type is consistent across all
 * @param templateSchemas
 * @returns
 */
export const mergeTemplateSchemasNew = (
  templateSchemas: Record<string, Partial<Record<WorkflowFieldKey, FieldSchema>>> = {},
): Partial<Record<WorkflowFieldKey, FieldSchema>> => {
  // 1. group schemas by path
  const groupedSchemas = groupSchemasByPath(
    Object.values(templateSchemas).flatMap((fieldKeyToFieldSchema): FieldSchema[] => {
      return Object.values(fieldKeyToFieldSchema);
    }),
  );

  // 2. filter out paths where schemas are not consistent
  const groupedSchemasFiltered: typeof groupedSchemas = Object.entries(groupedSchemas).reduce(
    (filtered, [path, fieldSchemas]) => {
      const [firstSchema, ...otherSchemas] = fieldSchemas;
      const typesAreConsistent = otherSchemas.every((fieldSchema) =>
        fieldTypesAreEqual(firstSchema, fieldSchema),
      );
      if (!typesAreConsistent) {
        return filtered;
      }
      return {
        ...filtered,
        [path]: fieldSchemas,
      };
    },
    {},
  );

  // 3. merge them all together
  return Object.entries(groupedSchemasFiltered).reduce<Record<string, FieldSchema>>(
    (merged, [path, fieldSchemas]) => {
      const firstSchema = fieldSchemas[0];

      switch (firstSchema.type) {
        case FieldSchemaType.Hash: {
          setMergedSchema(merged, path, {
            ...firstSchema,
            config: {
              ...firstSchema.config,
              'item-schemas': [], // will get populated as we iterate
            },
          });
          break;
        }
        case FieldSchemaType.Array: {
          if (firstSchema.config['item-schema'].type === FieldSchemaType.Hash) {
            setMergedSchema(merged, path, {
              ...firstSchema,
              config: {
                ...firstSchema.config,
                'item-schema': {
                  ...firstSchema.config['item-schema'],
                  config: {
                    ...firstSchema.config['item-schema'].config,
                    'item-schemas': [], // will get populated as we iterate
                  },
                },
              },
            });
          } else {
            setMergedSchema(merged, path, firstSchema);
          }
          break;
        }
        case FieldSchemaType.Choices:
        case FieldSchemaType.MultiChoices: {
          const optionsWithLabels = fieldSchemas.flatMap((schema) => {
            if (schema.type !== firstSchema.type) {
              return [];
            }
            const options = schema.config.options || [];
            const labels = schema.config['option-labels'] || [];
            return options.map((option, i) => ({ option, label: labels[i] }));
          });

          const filteredOptionsWithLabels = uniqBy('option', optionsWithLabels);
          setMergedSchema(merged, path, {
            ...firstSchema,
            config: {
              ...firstSchema.config,
              options: filteredOptionsWithLabels.map(({ option }) => option),
              'option-labels': filteredOptionsWithLabels.map(({ label }) => label),
            },
          });
          break;
        }
        default:
          setMergedSchema(merged, path, firstSchema);
      }

      return merged;
    },
    {},
  );
};

const getNestedSchemas = (fieldSchema: FieldSchema): FieldSchema[] => {
  if (fieldSchema.type === FieldSchemaType.Hash) {
    return fieldSchema.config['item-schemas'];
  }
  if (
    fieldSchema.type === FieldSchemaType.Array &&
    fieldSchema.config['item-schema'].type === FieldSchemaType.Hash
  ) {
    return fieldSchema.config['item-schema'].config['item-schemas'];
  }
  return [];
};

/**`
 * Groups field schemas by all possible levels by digging into all hashes. Example below:
 * @param fieldSchemas -> [
    {
      type: 'hash',
      key: 'fields',
      item-schemas: [
        {
          type: 'hash',
          key: 'address',
          item-schemas: [
            { type: 'string', key: 'street' },
            { type: 'string', key: 'city' },
          ],
        },
      ],
    },
    {
      type: 'hash',
      key: 'fields',
      item-schemas: [
        { type: 'string', key: 'name' },
        {
          type: 'hash',
          key: 'address',
          item-schemas: [
            { type: 'string', key: 'zipcode' },
          ],
        },
      ],
    },
  ]
 * @param prefix
 * @param initialGroupedSchemas
 * @returns -> {
    fields: [ { type: 'hash', key: 'fields', ... }, { type: 'hash', key: 'fields', ... }],
    fields.address: [ { type: 'hash', key: 'address', ... }, { type: 'hash', key: 'address', ... }],
    fields.address.street: [ { type: 'string', key: 'street' } ],
    fields.address.city: [ { type: 'string', key: 'city' } ],
    fields.address.zipcode: [ { type: 'string', key: 'zipcode' } ],
    fields.name: [ { type: 'string', key: 'name' } ],
  }
 */
export const groupSchemasByPath = (
  fieldSchemas: FieldSchema[],
  prefix?: string,
  initialGroupedSchemas: Record<string, FieldSchema[]> = {},
): Record<string, FieldSchema[]> => {
  return fieldSchemas.reduce((groupedSchemas, fieldSchema) => {
    const currentPath = prefix ? `${prefix}.${fieldSchema.key}` : fieldSchema.key;
    if (!groupedSchemas[currentPath]) {
      groupedSchemas[currentPath] = [];
    }
    groupedSchemas[currentPath].push(fieldSchema);

    const nestedSchemas = getNestedSchemas(fieldSchema);
    if (nestedSchemas.length) {
      return {
        ...groupedSchemas,
        ...groupSchemasByPath(nestedSchemas, currentPath, groupedSchemas),
      };
    }

    return { ...groupedSchemas };
  }, initialGroupedSchemas);
};

/**
 * This intentionally does not check hashes. For hashes, we don't require strict equality since we just combine them
 * @param fieldOne
 * @param fieldTwo
 * @returns
 */
export const fieldTypesAreEqual = (fieldOne: FieldSchema, fieldTwo: FieldSchema): boolean => {
  if (fieldOne.type === FieldSchemaType.Array && fieldTwo.type === FieldSchemaType.Array) {
    return fieldTypesAreEqual(fieldOne.config['item-schema'], fieldTwo.config['item-schema']);
  }
  return fieldOne.type === fieldTwo.type;
};

/**
 * Merges the provided schema into the appropriate hash within `merged`.
 * The target hash can be at any level, so `path` is used to determine the exact hash.
 * @param merged
 * @param path
 * @param schema
 * @returns
 */
export const setMergedSchema = (
  merged: Record<string, FieldSchema>,
  path: string,
  schema: FieldSchema,
) => {
  if (!path.includes('.')) {
    merged[path] = schema;
    return;
  }

  const pathKeys = path.split('.');
  // ignore last key when iterating
  const [firstPathKey, ...otherPathKeys] = pathKeys.slice(0, pathKeys.length - 1);
  let currentSchema: FieldSchema = merged[firstPathKey];
  otherPathKeys.forEach((pathKey: string) => {
    const nestedSchemas = getNestedSchemas(currentSchema);
    if (nestedSchemas.length) {
      const nestedSchema = nestedSchemas.find(({ key }) => key === pathKey);
      if (nestedSchema) {
        currentSchema = nestedSchema;
      }
    }
  });
  const nestedSchemas = getNestedSchemas(currentSchema);
  nestedSchemas.push(schema);
};

export const mergeTemplateSchemas = (
  templateSchemas: Record<string, Partial<Record<WorkflowFieldKey, FieldSchema>>> = {},
): Partial<Record<WorkflowFieldKey, FieldSchema>> => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return Object.values(templateSchemas).reduce((accum, fieldKeyToSchema) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return Object.keys(fieldKeyToSchema).reduce((result, fieldKey: WorkflowFieldKey) => {
      let overrideConfig = {};
      if (fieldKey in result) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const fieldType = result[fieldKey].type;

        if (fieldType === FieldSchemaType.Hash) {
          overrideConfig = {
            'item-schemas': [
              ...(result[fieldKey] as FieldSchemaHash).config['item-schemas'],
              ...(fieldKeyToSchema[fieldKey] as FieldSchemaHash).config['item-schemas'],
            ],
          };
        } else if (fieldType === FieldSchemaType.Choices) {
          overrideConfig = {
            options: [
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              ...(result[fieldKey] as FieldSchemaChoices).config['options'],
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              ...(fieldKeyToSchema[fieldKey] as FieldSchemaChoices).config['options'],
            ],
            ...((result[fieldKey] as FieldSchemaChoices).config['option-labels']
              ? {
                  ['option-labels']: [
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    ...(result[fieldKey] as FieldSchemaChoices).config['option-labels'],
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    ...(fieldKeyToSchema[fieldKey] as FieldSchemaChoices).config['option-labels'],
                  ],
                }
              : {}),
          };
        }
      }
      return {
        ...result,
        ...(isEmpty(overrideConfig)
          ? { [fieldKey]: fieldKeyToSchema[fieldKey] }
          : {
              [fieldKey]: {
                ...result[fieldKey],
                config: {
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  ...result[fieldKey].config,
                  ...overrideConfig,
                },
              },
            }),
      };
    }, accum);
  }, {});
};

export type ValueTransformer = {
  getDisplayValue: (value: string) => string;
  getTokenizedValue: (value: string) => string;
};
export const valueTransformer = (
  tokensToDisplayValue: Record<string, string>,
): ValueTransformer => {
  return {
    getDisplayValue: (value: string) => {
      try {
        return methodChainToString({
          ...EMPTY_METHOD_CHAIN,
          links: parseMethodChain(value).links.map((link) => {
            return {
              ...link,
              name: tokensToDisplayValue[link.name] ?? link.name,
            };
          }),
        });
      } catch (SyntaxError) {
        // fallback to splitting by dot which is the old way of doing this
        // this is for display so worst case we show non-human readable value
        return value
          .split('.')
          .map((part) => tokensToDisplayValue[part] ?? part)
          .join('.');
      }
    },
    getTokenizedValue: (value: string) => {
      try {
        return methodChainToString({
          ...EMPTY_METHOD_CHAIN,
          links: parseMethodChain(value).links.map((link) => {
            return {
              ...link,
              name: invert(tokensToDisplayValue)[link.name] ?? link.name,
            };
          }),
        });
      } catch (SyntaxError) {
        // fallback to splitting by dot which is the old way of doing this
        // this is for display so worst case we show non-human readable value
        return value
          .split('.')
          .map((part) => invert(tokensToDisplayValue)[part] ?? part)
          .join('.');
      }
    },
  };
};

export const getTokensToDisplayValue = (usableObjects: UsableObjectConfig[]) => {
  const counts: Record<string, number> = {};
  const tokensToDisplayValue: Record<string, string> = {};

  const registerTokenToDisplayValueMapping = (token: string, displayValue: string) => {
    if (!tokensToDisplayValue[token]) {
      if (!counts[displayValue]) {
        counts[displayValue] = 0;
      }
      counts[displayValue] += 1;

      const uniqueValue =
        counts[displayValue] > 1 ? `${displayValue}_${counts[displayValue]}` : displayValue;
      tokensToDisplayValue[token] = uniqueValue;
    }
  };

  usableObjects.forEach((usableObject) => {
    const { token, tokenDisplayValue } = usableObject;
    if (
      tokenDisplayValue &&
      !tokensToDisplayValue[token] &&
      ![BaseTrigger, BaseTriggerParams, INPUT_STEP_TOKEN].includes(token)
    ) {
      registerTokenToDisplayValueMapping(token, tokenDisplayValue);
    }
  });

  return tokensToDisplayValue;
};

export const generateOneJsonSchema = ({
  usableObject,
  criteriaVariableSchemas,
  platformObjectJsonSchema,
  genericWorkflowActions,
  useNewMergeStrategy = false,
}: {
  usableObject: UsableObjectConfig;
  criteriaVariableSchemas: WorkflowCriteriaVariableSchemas;
  platformObjectJsonSchema: ObjectSchemaMap | undefined;
  genericWorkflowActions: GenericWorkflowAction[];
  useNewMergeStrategy?: boolean;
}): JsonSchema | null => {
  const { value, workflowObjectKey, label, step, type, filters, isArray } = usableObject;
  const isTopLevel = value.split('.').length === 1;

  // IMPORTANT: schemas should be unique per workflowObjectKey
  switch (workflowObjectKey) {
    case WorkflowFieldVariableKey.TriggerAccount: {
      if (!('trigger' in step) || step.trigger?.type !== ApiModel.TriggerAccount) {
        return null;
      }
      return getAccountTriggerJsonSchema(
        step.trigger.attributes['input-modal-configuration'],
        label,
      );
    }
    case WorkflowFieldVariableKey.TriggerApi: {
      if (!('trigger' in step) || step.trigger?.type !== ApiModel.TriggerApi) {
        return null;
      }
      return getApiTriggerJsonSchema(step.trigger);
    }
    case WorkflowFieldVariableKey.TriggerCase: {
      if (!('trigger' in step) || step.trigger?.type !== ApiModel.TriggerCase) {
        return null;
      }
      return getCaseTriggerJsonSchema(step.trigger.attributes['param-schema'], label);
    }
    case WorkflowFieldVariableKey.InputsHash: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {},
      };
    }
    case WorkflowFieldVariableKey.InputVariable: {
      return usableObject.schema || null;
    }
    case WorkflowFieldVariableKey.Calculate: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {},
          },
        },
      };
    }
    case WorkflowFieldVariableKey.CalculateVariable: {
      return usableObject.schema || null;
    }
    case WorkflowFieldVariableKey.Code: {
      const action = (step as WorkflowStepAction).action;
      const primitiveTypeToJsonSchema = (type: WorkflowActionEvaluateCodeReturnType) => {
        switch (type) {
          case 'boolean':
            return { type: JsonSchemaTypes.Boolean };
          case 'string':
            return { type: JsonSchemaTypes.String };
          case 'number':
            return { type: JsonSchemaTypes.Number };
          default:
            return {};
        }
      };
      const returnSchema = (() => {
        if (action.version === 1) {
          const actionV1 = action as WorkflowActionEvaluateCodeV1;
          return primitiveTypeToJsonSchema(actionV1.params['return-type']);
        }

        if (action.version === 2) {
          const actionV2 = action as WorkflowActionEvaluateCodeV2;
          return JSON.parse(actionV2.params['return-schema']['config']['json-schema']);
        }

        return {};
      })();
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              result: returnSchema,
            },
          },
        },
      };
    }
    case WorkflowFieldVariableKey.HttpRequest: {
      const action = (step as WorkflowStepAction).action as WorkflowActionHttpRequestV1;
      return getHttpRequestJsonSchema(action.params, label);
    }
    case WorkflowFieldVariableKey.Matched: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              matched: {
                type: JsonSchemaTypes.Boolean,
              },
            },
          },
        },
      };
    }
    case WorkflowFieldVariableKey.Analytics: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              metric_value: {
                type: JsonSchemaTypes.Integer,
              },
              numerator: {
                type: JsonSchemaTypes.Integer,
              },
              denominator: {
                type: JsonSchemaTypes.Integer,
              },
            },
          },
        },
      };
    }
    case WorkflowFieldVariableKey.Loop: {
      return {
        ...getLoopStepInnerSchemaDefault(),
        description: label,
      };
    }
    case WorkflowFieldVariableKey.LoopCurrentItem: {
      const innerSchema = getLoopStepInnerSchemaCurrentItem(step as WorkflowStepLoop);
      if (!innerSchema) return null;
      return {
        ...innerSchema,
        description: label,
      };
    }
    case WorkflowFieldVariableKey.MapOutput: {
      const loopStep = step as WorkflowStepLoop;

      // output_array variable
      if (
        loopStep.settings.type === WorkflowStepLoopType.Map &&
        loopStep.settings['item-path'] &&
        loopStep.settings['output-item-schema']
      ) {
        return {
          type: JsonSchemaTypes.Object,
          description: label,
          properties: {
            output: {
              type: JsonSchemaTypes.Object,
              properties: {
                items_array: {
                  type: JsonSchemaTypes.Array,
                  items: loopStep.settings['output-item-schema'],
                },
              },
            },
          },
        };
      }
      return null;
    }
    case WorkflowFieldVariableKey.Module: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {},
      };
    }
    case WorkflowFieldVariableKey.Query: {
      const action = (step as WorkflowStepAction).action as WorkflowActionQueryV1;
      const objectType = action['params']['object-type'];
      if (!objectType) return null;

      const objectTypeToFieldObjectKey: Partial<
        Record<WorkflowActionQueryV1['params']['object-type'], WorkflowFieldObjectKey>
      > = {
        account: WorkflowFieldObjectKey.Account,
        case: WorkflowFieldObjectKey.Case,
        inquiry: WorkflowFieldObjectKey.Inquiry,
      };

      return {
        type: JsonSchemaTypes.Array,
        items: generateWorkflowObjectJsonSchema({
          criteriaVariableSchemas: criteriaVariableSchemas,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          objectKey: objectTypeToFieldObjectKey[objectType],
          description: label,
          filters: undefined,
          nestedObjectKeys: [],
          isTopLevel: isTopLevel,
          useNewMergeStrategy: useNewMergeStrategy,
        }),
      };
    }
    case WorkflowFieldVariableKey.SalesforceRecord: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              record_id: {
                type: JsonSchemaTypes.String,
              },
            },
          },
        },
      };
    }
    case WorkflowFieldVariableKey.ZendeskTicket: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              ticket_id: {
                type: JsonSchemaTypes.String,
              },
            },
          },
        },
      };
    }
    case WorkflowFieldVariableKey.GptGeneratedMessage: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              response: {
                type: JsonSchemaTypes.String,
              },
            },
          },
        },
      };
    }
    case WorkflowFieldVariableKey.MxUser: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              user_guid: {
                type: JsonSchemaTypes.String,
              },
            },
          },
        },
      };
    }
    case WorkflowFieldVariableKey.MxMicrodepositStatus: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              microdeposit_status: {
                type: JsonSchemaTypes.String,
              },
            },
          },
        },
      };
    }
    case WorkflowFieldVariableKey.MxWidgetUrl: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              widget_url: {
                type: JsonSchemaTypes.String,
              },
            },
          },
        },
      };
    }
    case WorkflowFieldVariableKey.IntegrationObjectId: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              object_id: {
                type: JsonSchemaTypes.String,
              },
            },
          },
        },
      };
    }
    case WorkflowFieldVariableKey.HubspotObjectId: {
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              object_id: {
                type: JsonSchemaTypes.String,
              },
            },
          },
        },
      };
    }
    case WorkflowFieldVariableKey.InquiryFlowStateProperty: {
      if (!type) return null;
      return { type, description: label };
    }
    case WorkflowFieldVariableKey.PlatformIntegration:
      if (
        step.type === WorkflowStepType.Action &&
        step.action != null &&
        step.action.key === WorkflowStepActionKey.RunHttpPlatformIntegration
      ) {
        const integration = step.action.params['integration'];
        const genericAction = genericWorkflowActions
          .filter(
            (genericAction): genericAction is HttpPlatformIntegrationConfig =>
              genericAction.key === WorkflowStepActionKey.RunHttpPlatformIntegration,
          )
          .find((platformIntegrationAction) =>
            isEqual(platformIntegrationAction.integration, integration),
          );
        return genericAction?.response?.schema as JsonSchema;
      }
      return null;
    case WorkflowFieldVariableKey.StandardizeIdDetails:
      return {
        type: JsonSchemaTypes.Object,
        description: label,
        properties: {
          output: {
            type: JsonSchemaTypes.Object,
            properties: {
              identification_number: { type: JsonSchemaTypes.String },
              identification_class: { type: JsonSchemaTypes.String },
              document_number: { type: JsonSchemaTypes.String },
            },
          },
        },
      };
    case WorkflowFieldObjectKey.Account:
    case WorkflowFieldObjectKey.Case:
    case WorkflowFieldObjectKey.CaseMessage:
    case WorkflowFieldObjectKey.Inquiry:
    case WorkflowFieldObjectKey.AccountType:
    case WorkflowFieldObjectKey.CaseTemplate:
    case WorkflowFieldObjectKey.DocumentGeneric:
    case WorkflowFieldObjectKey.DocumentGovernmentId:
    case WorkflowFieldObjectKey.Environment:
    case WorkflowFieldObjectKey.ReportCoinbaseCheckCryptoRisk:
    case WorkflowFieldObjectKey.GraphQuery:
    case WorkflowFieldObjectKey.InquiryTemplateVersion:
    case WorkflowFieldObjectKey.ReportAddressLookup:
    case WorkflowFieldObjectKey.ReportAdverseMedia:
    case WorkflowFieldObjectKey.ReportBusinessAdverseMedia:
    case WorkflowFieldObjectKey.ReportBusinessAssociatedPersons:
    case WorkflowFieldObjectKey.ReportBusinessClassification:
    case WorkflowFieldObjectKey.ReportBusinessIndustryClassification:
    case WorkflowFieldObjectKey.ReportBusinessLiensLookup:
    case WorkflowFieldObjectKey.ReportBusinessLookup:
    case WorkflowFieldObjectKey.ReportBusinessNonprofit:
    case WorkflowFieldObjectKey.ReportBusinessOnlinePresence:
    case WorkflowFieldObjectKey.ReportBusinessRegistrationsLookup:
    case WorkflowFieldObjectKey.ReportBusinessSocialMedia:
    case WorkflowFieldObjectKey.ReportBusinessWatchlist:
    case WorkflowFieldObjectKey.ReportBusinessWebsite:
    case WorkflowFieldObjectKey.ReportChainalysisAddressScreening:
    case WorkflowFieldObjectKey.ReportCryptoAddressWatchlist:
    case WorkflowFieldObjectKey.ReportCustomList:
    case WorkflowFieldObjectKey.ReportClearbitBusinessLookup:
    case WorkflowFieldObjectKey.ReportEmailAddress:
    case WorkflowFieldObjectKey.ReportEquifaxOneview:
    case WorkflowFieldObjectKey.ReportKyckrBusinessLookup:
    case WorkflowFieldObjectKey.ReportMiddeskBusinessLookup:
    case WorkflowFieldObjectKey.ReportMxAccount:
    case WorkflowFieldObjectKey.ReportPhoneNumber:
    case WorkflowFieldObjectKey.ReportPoliticallyExposedPerson:
    case WorkflowFieldObjectKey.ReportScreening:
    case WorkflowFieldObjectKey.ReportSocialMedia:
    case WorkflowFieldObjectKey.ReportSynthetic:
    case WorkflowFieldObjectKey.ReportTrmWalletScreening:
    case WorkflowFieldObjectKey.ReportWatchlist:
    case WorkflowFieldObjectKey.ReportProfile:
    case WorkflowFieldObjectKey.ReportProfileNonAuthoritative:
    case WorkflowFieldObjectKey.ReportSentilinkApplicationRisk:
    case WorkflowFieldObjectKey.ReportSentilinkScores:
    case WorkflowFieldObjectKey.ReportTemplate:
    case WorkflowFieldObjectKey.SAR:
    case WorkflowFieldObjectKey.SelfieFileImage:
    case WorkflowFieldObjectKey.Template:
    case WorkflowFieldObjectKey.Transaction:
    case WorkflowFieldObjectKey.TransactionType:
    case WorkflowFieldObjectKey.VerificationAamva:
    case WorkflowFieldObjectKey.VerificationBusinessWebsite:
    case WorkflowFieldObjectKey.VerificationCertificateKorea:
    case WorkflowFieldObjectKey.VerificationDatabase:
    case WorkflowFieldObjectKey.VerificationDatabaseAadhaar:
    case WorkflowFieldObjectKey.VerificationDatabaseBusiness:
    case WorkflowFieldObjectKey.VerificationDatabaseBusinessFootprint:
    case WorkflowFieldObjectKey.VerificationDatabaseEcbsv:
    case WorkflowFieldObjectKey.VerificationDatabasePhoneCarrier:
    case WorkflowFieldObjectKey.VerificationDatabaseSerpro:
    case WorkflowFieldObjectKey.VerificationDatabaseStandard:
    case WorkflowFieldObjectKey.VerificationDatabaseTin:
    case WorkflowFieldObjectKey.VerificationDatabaseVatNumber:
    case WorkflowFieldObjectKey.VerificationDigitalIdFinnishTrustNetwork:
    case WorkflowFieldObjectKey.VerificationDigitalIdOneId:
    case WorkflowFieldObjectKey.VerificationDigitalIdSingpass:
    case WorkflowFieldObjectKey.VerificationDigitalIdUkSharecode:
    case WorkflowFieldObjectKey.VerificationDocument:
    case WorkflowFieldObjectKey.VerificationEmailAddress:
    case WorkflowFieldObjectKey.VerificationGovernmentId:
    case WorkflowFieldObjectKey.VerificationGovernmentIdNfc:
    case WorkflowFieldObjectKey.VerificationMobileDriversLicense:
    case WorkflowFieldObjectKey.VerificationPhoneNumber:
    case WorkflowFieldObjectKey.User:
    case WorkflowFieldObjectKey.VerificationSelfie: {
      const objectSchema = platformObjectJsonSchema
        ? generatePlatformObjectJsonSchema({
            platformObjectJsonSchema,
            workflowObjectKey,
            filters,
          })
        : generateWorkflowObjectJsonSchema({
            criteriaVariableSchemas,
            objectKey: workflowObjectKey as WorkflowFieldObjectKey,
            description: label,
            filters,
            nestedObjectKeys: [],
            isTopLevel,
            useNewMergeStrategy,
          });
      return isArray ? { type: JsonSchemaTypes.Array, items: objectSchema } : objectSchema;
    }
    case WorkflowFieldObjectKey.Error: {
      return {
        type: JsonSchemaTypes.Object,
        properties: {
          error_message: { type: JsonSchemaTypes.String },
          error_type: {
            type: JsonSchemaTypes.String,
            enum: [
              WorkflowRunStepErrorType.UnknownError,
              WorkflowRunStepErrorType.UnresolvedPathError,
              WorkflowRunStepErrorType.InvalidStepConfigError,
              WorkflowRunStepErrorType.InvalidInputError,
              WorkflowRunStepErrorType.MissingInputError,
            ],
          },
        },
        description: label,
      };
    }
    case WorkflowFieldObjectKey.Generic: {
      return {
        type: JsonSchemaTypes.Object,
        properties: {},
        description: label,
      };
    }
    default:
      assertNever(workflowObjectKey);
  }
};

export const generateJsonSchemaConfig = ({
  usableObjects,
  useOriginal,
  previousJsonSchema,
}: {
  usableObjects: UsableObjectConfig[];
  useOriginal?: boolean;
  previousJsonSchema?: JsonSchemaObject;
}): {
  jsonSchema: JsonSchemaObject;
  tokensToDisplayValue: Record<string, string>;
} => {
  const tokensToDisplayValue = getTokensToDisplayValue(usableObjects);
  const valueGetter = useOriginal ? 'getTokenizedValue' : 'getDisplayValue';
  const valueFormatter = valueTransformer(tokensToDisplayValue)[valueGetter];
  const previousJsonSchemaProperties = previousJsonSchema ? previousJsonSchema.properties : {};
  const properties = usableObjects.reduce((hash: Record<string, JsonSchema>, usableObject) => {
    const { value, schema } = usableObject;
    // We insert 'properties' to account for any associations.
    // e.g. if value = 'trigger.account', it should add 'account' key under the trigger schema,
    // instead of adding a top level property 'trigger.account'.
    // Using lodash `set` below ensures this nesting happens.
    const formattedValue: string = valueFormatter(value)
      .split('.')
      .join('.properties.')
      .replace(/-/g, '_');

    if (schema) {
      return set(formattedValue, schema, hash);
    }

    return hash;
  }, previousJsonSchemaProperties);

  return {
    jsonSchema: {
      type: JsonSchemaTypes.Object,
      properties,
    },
    tokensToDisplayValue,
  };
};

export const generateStepContext = ({
  step,
  criteriaVariableSchemas,
  platformObjectJsonSchema,
  genericWorkflowActions,
  moduleBlock,
  includeBlockInnerContext,
  showErrorObjects,
}: {
  step: ContextualWorkflowStep;
  criteriaVariableSchemas: WorkflowCriteriaVariableSchemas;
  platformObjectJsonSchema: ObjectSchemaMap | undefined;
  genericWorkflowActions: GenericWorkflowAction[];
  moduleBlock?: WorkflowBlock;
  includeBlockInnerContext?: boolean;
  showErrorObjects?: boolean;
}): UsableObjectConfig[] => {
  const usableObjects = getAllObjectsForStep({
    step,
    criteriaVariableSchemas,
    moduleBlock,
    includeBlockInnerContext,
    platformObjectJsonSchema,
    showErrorObjects,
  });
  const fullUsableObjects = usableObjects.map((usableObject) => ({
    ...usableObject,
    schema: generateOneJsonSchema({
      usableObject,
      criteriaVariableSchemas,
      platformObjectJsonSchema,
      genericWorkflowActions,
    }),
  }));
  return fullUsableObjects;
};

const getHttpRequestJsonSchema = (
  params: WorkflowActionHttpRequestV1['params'],
  description: string,
): JsonSchemaObject => {
  const responseSchema = {
    type: JsonSchemaTypes.Object,
    properties: {
      response_status: { type: JsonSchemaTypes.Integer },
    },
  };

  const jsonSchemaString = params['return-schema']?.config['json-schema'];
  const parsedJson = jsonSchemaString && JSON.parse(jsonSchemaString);
  if (parsedJson && parsedJson.properties && Object.keys(parsedJson.properties).length) {
    responseSchema.properties = {
      ...responseSchema.properties,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [params['return-schema'].key]: parsedJson,
    };
  }

  return {
    type: JsonSchemaTypes.Object,
    description,
    properties: deepTransformKeys({
      output: {
        ...responseSchema,
      },
    }),
  };
};

const getApiTriggerJsonSchema = (trigger: TriggerApi): JsonSchemaObject | JsonSchemaEmpty => {
  const jsonFieldSchema = trigger.attributes['param-schema'][0];
  const jsonSchemaString = jsonFieldSchema.config['json-schema'];

  try {
    const parsedJson = jsonSchemaString && JSON.parse(jsonSchemaString);
    if (!parsedJson || !Object.keys(parsedJson).length) {
      return {};
    }

    return {
      type: JsonSchemaTypes.Object,
      properties: {
        [jsonFieldSchema.key]: parsedJson,
      },
    };
  } catch (e) {
    return {};
  }
};

const getCaseTriggerJsonSchema = (
  paramSchema: TriggerCaseParamSchema[],
  description: string,
): JsonSchemaObject => {
  const triggerParamProperties = paramSchema.reduce((attrs: Record<string, JsonSchema>, key) => {
    const { name, type } = key;

    switch (type) {
      case InputSchemaType.Dropdown:
      case InputSchemaType.Input:
      case InputSchemaType.Phone:
      case InputSchemaType.TextField:
      case InputSchemaType.MaskedInput:
      case InputSchemaType.Date:
      case InputSchemaType.Country:
      case InputSchemaType.USState:
      case InputSchemaType.USStateCode:
      case InputSchemaType.RadioGroup:
        attrs[name] = {
          type: JsonSchemaTypes.String,
        };
        break;
      case InputSchemaType.Address:
        attrs[name] = {
          type: JsonSchemaTypes.Object,
          properties: {
            street_1: {
              type: JsonSchemaTypes.String,
            },
            street_2: {
              type: JsonSchemaTypes.String,
            },
            city: {
              type: JsonSchemaTypes.String,
            },
            subdivision: {
              type: JsonSchemaTypes.String,
            },
            postal_code: {
              type: JsonSchemaTypes.String,
            },
            country_code: {
              type: JsonSchemaTypes.String,
            },
          },
        };
        break;
      case InputSchemaType.CheckBox:
        attrs[name] = {
          type: JsonSchemaTypes.Boolean,
        };
        break;
      default:
        assertNever(type);
    }
    return attrs;
  }, {});

  return {
    type: JsonSchemaTypes.Object,
    description,
    properties: triggerParamProperties,
  };
};

const getAccountTriggerJsonSchema = (
  inputModalConfiguration: TriggerAccountInputModalConfiguration[],
  description: string,
): JsonSchemaObject => {
  const triggerParamProperties = inputModalConfiguration.reduce(
    (attrs: Record<string, JsonSchema>, item) => {
      const { key, type } = item;

      switch (type) {
        case InputSchemaType.Dropdown:
        case InputSchemaType.Input:
        case InputSchemaType.Phone:
        case InputSchemaType.TextField:
        case InputSchemaType.MaskedInput:
        case InputSchemaType.Date:
        case InputSchemaType.Country:
        case InputSchemaType.USState:
        case InputSchemaType.USStateCode:
        case InputSchemaType.RadioGroup:
          attrs[key] = {
            type: JsonSchemaTypes.String,
          };
          break;
        case InputSchemaType.Address:
          //TODO(zixiao): match json structure with AddressInput component
          attrs[key] = {
            type: JsonSchemaTypes.Object,
            properties: {
              street1: {
                type: JsonSchemaTypes.String,
              },
              street2: {
                type: JsonSchemaTypes.String,
              },
              city: {
                type: JsonSchemaTypes.String,
              },
              subdivision: {
                type: JsonSchemaTypes.String,
              },
              postalCode: {
                type: JsonSchemaTypes.String,
              },
              countryCode: {
                type: JsonSchemaTypes.String,
              },
            },
          };
          break;
        case InputSchemaType.CheckBox:
          attrs[key] = {
            type: JsonSchemaTypes.Boolean,
          };
          break;
        default:
          assertNever(type);
      }
      return attrs;
    },
    {},
  );

  return {
    type: JsonSchemaTypes.Object,
    description,
    properties: triggerParamProperties,
  };
};

const getLoopStepInnerSchemaDefault = (
  additionalProps: Record<string, JsonSchema> = {},
): JsonSchemaObject => {
  return {
    type: JsonSchemaTypes.Object,
    properties: {
      output: {
        type: JsonSchemaTypes.Object,
        properties: {
          current_iteration: {
            type: JsonSchemaTypes.Integer,
          },
          ...additionalProps,
        },
      },
    },
  };
};

const getLoopStepInnerSchemaCurrentItem = (step: WorkflowStepLoop) => {
  if (
    (step.settings.type === WorkflowStepLoopType.ForEach ||
      step.settings.type === WorkflowStepLoopType.Map) &&
    step.settings['input-array-schema']
  ) {
    const inputArraySchema = step.settings['input-array-schema'];
    if (inputArraySchema.type === JsonSchemaTypes.Array && inputArraySchema.items) {
      return inputArraySchema.items;
    }
  }
  return null;
};

export const getLoopStepInnerSchema = (step: WorkflowStepLoop): JsonSchemaObject => {
  const currentItemSchema = getLoopStepInnerSchemaCurrentItem(step);
  const additionalProps: Record<string, JsonSchema> = currentItemSchema
    ? { current_item: currentItemSchema }
    : {};
  return getLoopStepInnerSchemaDefault(additionalProps);
};

export const REPORT_OBJECT_KEYS: WorkflowFieldObjectKey[] = [
  WorkflowFieldObjectKey.ReportAddressLookup,
  WorkflowFieldObjectKey.ReportAdverseMedia,
  WorkflowFieldObjectKey.ReportBusinessAdverseMedia,
  WorkflowFieldObjectKey.ReportBusinessAssociatedPersons,
  WorkflowFieldObjectKey.ReportBusinessClassification,
  WorkflowFieldObjectKey.ReportBusinessIndustryClassification,
  WorkflowFieldObjectKey.ReportBusinessLiensLookup,
  WorkflowFieldObjectKey.ReportBusinessLookup,
  WorkflowFieldObjectKey.ReportBusinessNonprofit,
  WorkflowFieldObjectKey.ReportBusinessOnlinePresence,
  WorkflowFieldObjectKey.ReportBusinessRegistrationsLookup,
  WorkflowFieldObjectKey.ReportBusinessSocialMedia,
  WorkflowFieldObjectKey.ReportBusinessWatchlist,
  WorkflowFieldObjectKey.ReportBusinessWebsite,
  WorkflowFieldObjectKey.ReportChainalysisAddressScreening,
  WorkflowFieldObjectKey.ReportClearbitBusinessLookup,
  WorkflowFieldObjectKey.ReportCoinbaseCheckCryptoRisk,
  WorkflowFieldObjectKey.ReportCryptoAddressWatchlist,
  WorkflowFieldObjectKey.ReportCustomList,
  WorkflowFieldObjectKey.ReportEmailAddress,
  WorkflowFieldObjectKey.ReportEquifaxOneview,
  WorkflowFieldObjectKey.ReportKyckrBusinessLookup,
  WorkflowFieldObjectKey.ReportMiddeskBusinessLookup,
  WorkflowFieldObjectKey.ReportMxAccount,
  WorkflowFieldObjectKey.ReportPhoneNumber,
  WorkflowFieldObjectKey.ReportPoliticallyExposedPerson,
  WorkflowFieldObjectKey.ReportProfile,
  WorkflowFieldObjectKey.ReportProfileNonAuthoritative,
  WorkflowFieldObjectKey.ReportScreening,
  WorkflowFieldObjectKey.ReportSentilinkApplicationRisk,
  WorkflowFieldObjectKey.ReportSentilinkScores,
  WorkflowFieldObjectKey.ReportSocialMedia,
  WorkflowFieldObjectKey.ReportSynthetic,
  WorkflowFieldObjectKey.ReportTrmWalletScreening,
  WorkflowFieldObjectKey.ReportWatchlist,
];

export const VERIFICATION_OBJECT_KEYS: WorkflowFieldObjectKey[] = [
  WorkflowFieldObjectKey.VerificationAamva,
  WorkflowFieldObjectKey.VerificationBusinessWebsite,
  WorkflowFieldObjectKey.VerificationCertificateKorea,
  WorkflowFieldObjectKey.VerificationDatabase,
  WorkflowFieldObjectKey.VerificationDatabaseAadhaar,
  WorkflowFieldObjectKey.VerificationDatabaseBusiness,
  WorkflowFieldObjectKey.VerificationDatabaseBusinessFootprint,
  WorkflowFieldObjectKey.VerificationDatabaseEcbsv,
  WorkflowFieldObjectKey.VerificationDatabasePhoneCarrier,
  WorkflowFieldObjectKey.VerificationDatabaseSerpro,
  WorkflowFieldObjectKey.VerificationDatabaseStandard,
  WorkflowFieldObjectKey.VerificationDatabaseTin,
  WorkflowFieldObjectKey.VerificationDatabaseVatNumber,
  WorkflowFieldObjectKey.VerificationDigitalIdFinnishTrustNetwork,
  WorkflowFieldObjectKey.VerificationDigitalIdOneId,
  WorkflowFieldObjectKey.VerificationDigitalIdSingpass,
  WorkflowFieldObjectKey.VerificationDigitalIdUkSharecode,
  WorkflowFieldObjectKey.VerificationDocument,
  WorkflowFieldObjectKey.VerificationEmailAddress,
  WorkflowFieldObjectKey.VerificationGovernmentId,
  WorkflowFieldObjectKey.VerificationGovernmentIdNfc,
  WorkflowFieldObjectKey.VerificationMobileDriversLicense,
  WorkflowFieldObjectKey.VerificationPhoneNumber,
  WorkflowFieldObjectKey.VerificationSelfie,
];

/**
 * Filters a list of steps to only return the ones that brings a case into the workflow. (e.g. create case, fetch Persona object)
 */
export const caseGetSteps = (steps: WorkflowStep[]) => {
  return steps.filter((step): step is WorkflowStepAction => {
    return (
      // create case
      (step.type === WorkflowStepType.Action &&
        step.action != null &&
        step.action.key === WorkflowStepActionKey.CreateCase) ||
      // fetch object with type case
      (step.type === WorkflowStepType.Action &&
        step.action != null &&
        step.action.key === WorkflowStepActionKey.FetchPersonaObject &&
        (step.action as WorkflowActionFetchPersonaObjectV1).params['object-type'] === ApiModel.Case)
    );
  });
};

export const taggableObjectsFor = (usableObjects: UsableObjectConfig[]) => {
  return filterUsableObjects({
    usableObjects,
    options: {
      permittedObjects: [
        WorkflowFieldObjectKey.Inquiry,
        WorkflowFieldObjectKey.Account,
        WorkflowFieldObjectKey.Case,
        WorkflowFieldObjectKey.Transaction,
        ...REPORT_OBJECT_KEYS,
      ],
      excludeNonPersonaObjects: true,
    },
  });
};

export enum Operator {
  Set = 'set',
  Unset = 'unset',
  // Array types
  // append means add two arrays together
  Append = 'append',
  // push means add one element to an array
  Push = 'push',
  // filter matching elements from an array
  Remove = 'remove',
}

export interface KeyValue {
  [key: string]: string | number | boolean | KeyValue;
}

// Flattens a (possibly) nested hash into an array of [string,string|number|boolean], where the first string is
// the path to the original element in dot separated notation, and the second element is the original value
export const flattenKeyValue = <Result extends string | number | boolean>(
  keyValue: KeyValue,
  path: string[] = [],
): [string, Result][] => {
  if (keyValue == null) {
    return [];
  }
  return Object.entries(keyValue).flatMap((entry): [string, Result][] => {
    const [key, value] = entry;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return [[[...path, key].join('.'), value as Result]];
    } else {
      return flattenKeyValue(value, [...path, key]);
    }
  });
};

interface PrimitiveKeyValue {
  [key: string]: string | boolean | number;
}

export interface StringKeyValue {
  [key: string]: string | StringKeyValue;
}

export const flattenedEntriesToHash = <T extends PrimitiveKeyValue>(entries: T) => {
  return Object.entries(entries).reduce(
    (acc, [key, value]) => set(key.split('.'), value, acc),
    {} as T,
  );
};

// fieldsAreCompatible returns true if left and right are the same type
// left and right can be arrays or objects.
// if they are arrays, then all of the properties of right's items exist in left's items (i.e. right.item can be assigned
// to left.item)
// if they are objects, then all of the properties of right exist in left, and are of the same type (i.e. right can be
// assigned to left)
const fieldsAreCompatible = (left: JsonSchema, right: JsonSchema): boolean => {
  const leftJsonSchemaType = getJsonSchemaType(left);
  const rightJsonSchemaType = getJsonSchemaType(right);
  if (leftJsonSchemaType !== rightJsonSchemaType) {
    return false;
  }
  if (leftJsonSchemaType === JsonSchemaTypes.Array) {
    const leftItems = (left as JsonSchemaArray).items;
    const rightItems = (right as JsonSchemaArray).items;
    // this shouldn't happen but we are doing this to appease the compiler
    if (leftItems == null && rightItems == null) {
      return true;
    }
    if (leftItems == null || rightItems == null) {
      return false;
    }
    return fieldsAreCompatible(leftItems, rightItems);
  }
  if (leftJsonSchemaType === JsonSchemaTypes.Object) {
    const leftSchema = left as JsonSchemaObject;
    const rightSchema = right as JsonSchemaObject;
    // if right has no properties we assume it can be assigned to left
    if (rightSchema.properties == null || Object.keys(rightSchema.properties).length === 0) {
      return true;
    }
    const leftProperties = leftSchema.properties;
    if (leftProperties == null) {
      return false;
    }
    return Object.entries(rightSchema.properties).every(([field, jsonSchema]) => {
      const sourceFieldType = getJsonSchemaType(jsonSchema);
      const targetField = leftProperties[field.replace(/_/g, '-')];
      if (targetField == null) {
        return false;
      }
      if (sourceFieldType !== getJsonSchemaType(targetField)) {
        return false;
      }
      if (sourceFieldType === JsonSchemaTypes.Array || sourceFieldType === JsonSchemaTypes.Object) {
        return fieldsAreCompatible(targetField, jsonSchema);
      }
      return true;
    });
  }
  return true;
};

// given target, which is of JsonSchemaType object, we try to find a path to a matching object/array in
// candidate.
// if target is an array, that matching array's item properties must be a subset of the item properties in target
// if target is an object, that matching object's properties must be a subset of the properties in target
export const findMatchingObjectOrArrayPaths = (
  target: JsonSchema,
  candidate: JsonSchema,
  path: string[] = [],
) => {
  const targetType = getJsonSchemaType(target);
  const candidateType = getJsonSchemaType(candidate);
  if (candidateType === JsonSchemaTypes.Array) {
    if (targetType === JsonSchemaTypes.Array) {
      // if the candidate is an array, then we do not recurse into the items using findMatchingObjectPaths because
      // we cannot address children of arrays properly.
      //
      // i.e. if target is an object of type { field1: object, field2: object }
      // and candidate.b is an array, and candidate.b's items is of type { field1: object, field2: object }, then
      // candidate.b.field1 and candidate.b.field2 cannot be matching objects for target since we can't pick one of them
      // out of the array
      return fieldsAreCompatible(target, candidate) ? [path.join('.')] : [];
    }
    return [];
  } else if (candidateType === JsonSchemaTypes.Object) {
    const schema = candidate as JsonSchemaObject;
    if (schema.properties == null || Object.keys(candidate).length === 0) {
      if (targetType === JsonSchemaTypes.Object) {
        return [path.join('.')];
      }
      return [];
    }
    const matchingPaths: string[] = [];
    if (fieldsAreCompatible(target, candidate)) {
      matchingPaths.push(path.join('.'));
    }
    Object.entries(schema.properties).forEach(([key, jsonSchema]) => {
      if (fieldsAreCompatible(target, jsonSchema)) {
        matchingPaths.push([...path, key].join('.'));
      }
      const jsonSchemaType = getJsonSchemaType(jsonSchema);
      if (jsonSchemaType === JsonSchemaTypes.Object || jsonSchemaType === JsonSchemaTypes.Array) {
        matchingPaths.push(...findMatchingObjectOrArrayPaths(target, jsonSchema, [...path, key]));
      }
    });
    return matchingPaths;
  } else {
    throw new Error('unexpected type');
  }
};
